import { useEffect, useState } from "react";
import { Loader2, LogOut, Plus, Trash2, Save, Users, FileText, BarChart3, HelpCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const inputCls = "w-full bg-[#181C28] border border-[#262B3D] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-primary transition-colors";

const Login = ({ onLogin }) => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [state, setState] = useState({ loading: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      const { data } = await api.post("/auth/login", form);
      onLogin(data);
    } catch (err) {
      setState({ loading: false, error: formatApiError(err) });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0C10] grain relative px-4">
      <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/[0.06] blur-[120px]" />
      <form onSubmit={submit} data-testid="admin-login-form" className="relative glass-card p-8 sm:p-10 w-full max-w-md space-y-5">
        <div className="flex items-center gap-2 mb-2">
          <img src="/logo-icon.png" alt="La Bascule" className="w-9 h-9 rounded-lg" />
          <span className="font-heading font-bold text-lg">Admin — La Bascule</span>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Email</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Mot de passe</label>
          <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password-input" className={inputCls} />
        </div>
        <button type="submit" disabled={state.loading} data-testid="admin-login-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-primary text-[#0A0C10] font-semibold px-6 py-3.5 rounded-full hover:bg-[#00C885] transition-colors disabled:opacity-60">
          {state.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
        </button>
        {state.error && <p data-testid="admin-login-error" className="text-sm text-red-400 text-center">{state.error}</p>}
      </form>
    </div>
  );
};

const Leads = ({ leads }) => (
  <div className="space-y-10">
    <div>
      <h3 className="font-heading text-lg font-bold mb-4">Candidatures accompagnement ({leads.applications.length})</h3>
      <div className="space-y-3" data-testid="admin-applications-list">
        {leads.applications.length === 0 && <p className="text-sm text-slate-500">Aucune candidature pour le moment.</p>}
        {leads.applications.map((a) => (
          <div key={a.id} data-testid={`admin-application-${a.id}`} className="glass-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-sm">{a.name} <span className="text-slate-500 font-normal">· {a.email}</span></p>
              <p className="text-xs text-slate-400 mt-1">
                {a.situation}{a.tjm ? ` · TJM ${a.tjm}€` : ""} · {new Date(a.created_at).toLocaleDateString("fr-FR")}
              </p>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{a.probleme}</p>
            </div>
            <select
              value={a.status}
              onChange={(e) => api.patch(`/admin/applications/${a.id}`, { status: e.target.value })}
              data-testid={`admin-application-status-${a.id}`}
              className={`${inputCls} sm:w-40`}
            >
              <option value="nouveau">Nouveau</option>
              <option value="contacte">Contacté</option>
              <option value="appele_planifie">Appel planifié</option>
              <option value="refuse">Refusé</option>
            </select>
          </div>
        ))}
      </div>
    </div>
    <div>
      <h3 className="font-heading text-lg font-bold mb-4">Réservations workshop ({(leads.workshop_bookings || []).length})</h3>
      <div className="space-y-3" data-testid="admin-workshop-list">
        {(leads.workshop_bookings || []).length === 0 && <p className="text-sm text-slate-500">Aucune réservation pour le moment.</p>}
        {(leads.workshop_bookings || []).map((b) => (
          <div key={b.ref} data-testid={`admin-workshop-${b.ref}`} className="glass-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">{b.name} <span className="text-slate-500 font-normal">· {b.email}</span></p>
              <p className="text-xs text-slate-400 mt-1">{b.profession}{b.phone ? ` · ${b.phone}` : ""} · {new Date(b.created_at).toLocaleDateString("fr-FR")}</p>
              <p className="text-xs font-mono text-slate-500 mt-1">Réf {b.ref}</p>
            </div>
            <span className={`text-xs font-mono px-3 py-1 rounded-full border ${b.confirmation_sent ? "border-primary/40 text-primary bg-primary/10" : "border-[#262B3D] text-slate-500"}`}>
              {b.confirmation_sent ? "Email envoyé ✓" : "Email en attente"}
            </span>
          </div>
        ))}
      </div>
    </div>
    <div>
      <h3 className="font-heading text-lg font-bold mb-4">Abonnés newsletter ({leads.subscribers.length})</h3>
      <div className="glass-card divide-y divide-[#262B3D]" data-testid="admin-subscribers-list">
        {leads.subscribers.length === 0 && <p className="text-sm text-slate-500 p-5">Aucun abonné pour le moment.</p>}
        {leads.subscribers.map((s) => (
          <div key={s.email} className="p-4 flex items-center justify-between">
            <span className="text-sm">{s.email}</span>
            <span className="text-xs font-mono text-slate-500">
              {new Date(s.created_at).toLocaleDateString("fr-FR")} · {s.status === "synced" ? <span className="text-primary">MailerLite ✓</span> : s.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ContentEditor = ({ content, onSaved }) => {
  const [data, setData] = useState(content);
  const [saving, setSaving] = useState(false);
  const uid = () => Math.random().toString(36).slice(2, 9);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", {
        marquee: data.marquee,
        stats: data.stats,
        testimonials: data.testimonials,
        faqs: data.faqs,
      });
      onSaved(data);
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (key, id, field, value) =>
    setData({ ...data, [key]: data[key].map((it) => (it.id === id ? { ...it, [field]: value } : it)) });
  const removeItem = (key, id) => setData({ ...data, [key]: data[key].filter((it) => it.id !== id) });

  return (
    <div className="space-y-10">
      <div>
        <h3 className="font-heading text-lg font-bold mb-4">Témoignages</h3>
        <div className="space-y-4" data-testid="admin-testimonials-editor">
          {data.testimonials.map((t) => (
            <div key={t.id} className="glass-card p-5 space-y-3">
              <div className="flex justify-between gap-3">
                <input value={t.result} onChange={(e) => updateItem("testimonials", t.id, "result", e.target.value)} placeholder="Résultat chiffré" className={inputCls} data-testid={`admin-testimonial-result-${t.id}`} />
                <button onClick={() => removeItem("testimonials", t.id)} data-testid={`admin-testimonial-delete-${t.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea rows={2} value={t.quote} onChange={(e) => updateItem("testimonials", t.id, "quote", e.target.value)} placeholder="Citation" className={`${inputCls} resize-none`} />
              <div className="grid grid-cols-2 gap-3">
                <input value={t.name} onChange={(e) => updateItem("testimonials", t.id, "name", e.target.value)} placeholder="Nom" className={inputCls} />
                <input value={t.role} onChange={(e) => updateItem("testimonials", t.id, "role", e.target.value)} placeholder="Rôle" className={inputCls} />
              </div>
            </div>
          ))}
          <button
            onClick={() => setData({ ...data, testimonials: [...data.testimonials, { id: uid(), result: "", quote: "", name: "", role: "" }] })}
            data-testid="admin-testimonial-add"
            className="inline-flex items-center gap-2 text-sm text-primary border border-primary/40 rounded-full px-5 py-2.5 hover:bg-primary/10 transition-colors"
          >
            <Plus className="w-4 h-4" /> Ajouter un témoignage
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-bold mb-4">Chiffres clés</h3>
        <div className="space-y-3" data-testid="admin-stats-editor">
          {data.stats.map((s) => (
            <div key={s.id} className="glass-card p-4 grid grid-cols-[1fr_90px_80px_40px] gap-3 items-center">
              <input value={s.label} onChange={(e) => updateItem("stats", s.id, "label", e.target.value)} placeholder="Label" className={inputCls} />
              <input type="number" step="any" value={s.value} onChange={(e) => updateItem("stats", s.id, "value", parseFloat(e.target.value) || 0)} className={inputCls} data-testid={`admin-stat-value-${s.id}`} />
              <input value={s.suffix} onChange={(e) => updateItem("stats", s.id, "suffix", e.target.value)} placeholder="Suffixe" className={inputCls} />
              <button onClick={() => removeItem("stats", s.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button
            onClick={() => setData({ ...data, stats: [...data.stats, { id: uid(), value: 0, suffix: "", decimals: 0, label: "" }] })}
            data-testid="admin-stat-add"
            className="inline-flex items-center gap-2 text-sm text-primary border border-primary/40 rounded-full px-5 py-2.5 hover:bg-primary/10 transition-colors"
          >
            <Plus className="w-4 h-4" /> Ajouter un chiffre
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-bold mb-4">FAQ</h3>
        <div className="space-y-4" data-testid="admin-faqs-editor">
          {data.faqs.map((f) => (
            <div key={f.id} className="glass-card p-5 space-y-3">
              <div className="flex justify-between gap-3">
                <input value={f.q} onChange={(e) => updateItem("faqs", f.id, "q", e.target.value)} placeholder="Question" className={inputCls} />
                <button onClick={() => removeItem("faqs", f.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea rows={2} value={f.a} onChange={(e) => updateItem("faqs", f.id, "a", e.target.value)} placeholder="Réponse" className={`${inputCls} resize-none`} />
            </div>
          ))}
          <button
            onClick={() => setData({ ...data, faqs: [...data.faqs, { id: uid(), q: "", a: "" }] })}
            data-testid="admin-faq-add"
            className="inline-flex items-center gap-2 text-sm text-primary border border-primary/40 rounded-full px-5 py-2.5 hover:bg-primary/10 transition-colors"
          >
            <Plus className="w-4 h-4" /> Ajouter une question
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-bold mb-4">Bandeau défilant</h3>
        <div className="space-y-3">
          {data.marquee.map((m, i) => (
            <div key={i} className="flex gap-3 items-center">
              <input value={m} onChange={(e) => setData({ ...data, marquee: data.marquee.map((x, j) => (j === i ? e.target.value : x)) })} className={inputCls} />
              <button onClick={() => setData({ ...data, marquee: data.marquee.filter((_, j) => j !== i) })} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button
            onClick={() => setData({ ...data, marquee: [...data.marquee, ""] })}
            data-testid="admin-marquee-add"
            className="inline-flex items-center gap-2 text-sm text-primary border border-primary/40 rounded-full px-5 py-2.5 hover:bg-primary/10 transition-colors"
          >
            <Plus className="w-4 h-4" /> Ajouter une ligne
          </button>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="admin-content-save"
        className="sticky bottom-6 inline-flex items-center gap-2 bg-primary text-[#0A0C10] font-semibold px-8 py-4 rounded-full hover:bg-[#00C885] transition-colors disabled:opacity-60 shadow-[0_10px_40px_rgba(0,229,153,0.3)]">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Enregistrer le contenu
      </button>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState(null);
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, [user]);

  if (user === undefined) {
    return <div className="min-h-screen bg-[#0A0C10] flex items-center justify-center"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>;
  }
  if (user === null) return <Login onLogin={setUser} />;

  const tabs = [
    { id: "leads", label: "Leads", icon: Users },
    { id: "content", label: "Contenu du site", icon: FileText },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0C10]">
      <header className="border-b border-[#262B3D] bg-[#0A0C10]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="container-site h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-icon.png" alt="La Bascule" className="w-8 h-8 rounded-lg" />
            <span className="font-heading font-bold">Admin La Bascule</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500 hidden sm:block">{user.email}</span>
            <button
              onClick={async () => { await api.post("/auth/logout"); setUser(null); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="container-site py-10">
        <div className="flex gap-3 mb-10">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                tab === t.id ? "bg-primary text-[#0A0C10]" : "border border-[#262B3D] text-slate-400 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "leads" && (leads ? <Leads leads={leads} /> : <Loader2 className="w-5 h-5 text-primary animate-spin" />)}
        {tab === "content" && (content ? (
          <ContentEditor content={content} onSaved={(d) => setContent(d)} />
        ) : <Loader2 className="w-5 h-5 text-primary animate-spin" />)}
      </main>
    </div>
  );
}
